// src/components/Layout.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import "../App.css";
import "./Layout.css";

import CartBadge from "./CartBadge";
import NavLoginButton from "./NavLoginButton";
import WhatsAppButton from "./WhatsAppButton";
import Footer from "./Footer";
import { FaWhatsapp } from "react-icons/fa";

import LogoDark from "../assets/images/LOGOFINAL0.webp";
import LogoLight from "../assets/images/LOGOFINAL0.webp";

import { getCart } from "../cart/cart-utils";
// import CookieConsent from "../components/CookieConsent";
const API_URL =
  process.env.REACT_APP_API ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.lucianacosta.cv";

/* -------- Helpers -------- */
function displayName(u) {
  if (!u) return "Conta";
  const name = u?.name || u?.nome || u?.given_name || "";
  if (name) return String(name).trim().split(" ")[0];
  const email = u?.email || "";
  return email ? email.split("@")[0] : "Conta";
}

function UserGlyph(props) {
  return (
    <svg viewBox="0 0 50 50" role="img" focusable="false" aria-hidden="true" {...props}>
      <path
        d="M25 48.077c-5.924 0-11.31-2.252-15.396-5.921 2.254-5.362 7.492-8.267 15.373-8.267 7.889 0 13.139 3.044 15.408 8.418-4.084 3.659-9.471 5.77-15.385 5.77m.278-35.3c4.927 0 8.611 3.812 8.611 8.878 0 5.21-3.875 9.456-8.611 9.456s-8.611-4.246-8.611-9.456c0-5.066 3.684-8.878 8.611-8.878M25 0C11.193 0 0 11.193 0 25c0 .915.056 1.816.152 2.705.032.295.091.581.133.873.085.589.173 1.176.298 1.751.073.338.169.665.256.997.135.515.273 1.027.439 1.529.114.342.243.675.37 1.01.18.476.369.945.577 1.406.149.331.308.657.472.98.225.446.463.883.714 1.313.182.312.365.619.56.922.272.423.56.832.856 1.237.207.284.41.568.629.841.325.408.671.796 1.02 1.182.22.244.432.494.662.728.405.415.833.801 1.265 1.186.173.154.329.325.507.475l.004-.011A24.886 24.886 0 0 0 25 50a24.881 24.881 0 0 0 16.069-5.861.126.126 0 0 1 .003.01c.172-.144.324-.309.49-.458.442-.392.88-.787 1.293-1.209.228-.232.437-.479.655-.72.352-.389.701-.78 1.028-1.191.218-.272.421-.556.627-.838.297-.405.587-.816.859-1.24a26.104 26.104 0 0 0 1.748-3.216c.208-.461.398-.93.579-1.406.127-.336.256-.669.369-1.012.167-.502.305-1.014.44-1.53.087-.332.183-.659.256-.996.126-.576.214-1.164.299-1.754.042-.292.101-.577.133-.872.095-.89.152-1.791.152-2.707C50 11.193 38.807 0 25 0"
        fill="currentColor"
      />
    </svg>
  );
}

function getCartQty() {
  try {
    const items = getCart();
    return items.reduce((sum, it) => sum + (Number(it?.qty) || 0), 0);
  } catch {
    return 0;
  }
}

function smoothScrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
  window.scrollTo({ top, behavior: prefersReduced ? "auto" : "smooth" });
}

function closeMobileMenu() {
  const el = document.getElementById("navMain");
  if (!el) return;
  if (el.classList.contains("show")) {
    try {
      const bs =
        window.bootstrap?.Collapse.getInstance(el) ||
        new window.bootstrap.Collapse(el, { toggle: false });
      bs.hide();
    } catch {
      el.classList.remove("show");
      el.style.height = "";
    }
  }
}

/* -------- Component -------- */
export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [cartQty, setCartQty] = useState(() => getCartQty());

  // qty e collapse ao mudar de rota
  useEffect(() => {
    setCartQty(getCartQty());
    const collapse = document.getElementById("navMain");
    if (collapse?.classList.contains("show")) {
      const evt = new window.Event("click", { bubbles: true });
      document.querySelector(".navbar-toggler")?.dispatchEvent(evt);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onStorage = (e) => { if (e.key === "cart") setCartQty(getCartQty()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  useEffect(() => {
    const onCartUpdated = () => setCartQty(getCartQty());
    window.addEventListener("cart-updated", onCartUpdated);
    return () => window.removeEventListener("cart-updated", onCartUpdated);
  }, []);

  /* ---------- Sessão (JWT OU user local OU cookie httpOnly) ---------- */
  const token = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");

  // Tenta obter user via cookie quando não há nada em localStorage (login social recente)
  useEffect(() => {
    if (token || storedUser) return;
    fetch(`${API_URL}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (j?.ok && j?.user) {
          localStorage.setItem("user", JSON.stringify(j.user));
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const utilizador = useMemo(() => {
    try {
      if (token) return jwtDecode(token);
      if (storedUser) return JSON.parse(storedUser);
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
    return null;
  }, [token, storedUser]);

  // Sair: limpa tudo e termina sessão no backend (cookie)
  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } finally {
      fetch(`${API_URL}/auth/logout`, { method: "POST", credentials: "include" })
        .finally(() => navigate("/login"));
    }
  }, [navigate]);

  const isHome = location.pathname === "/";
  const isLogin = location.pathname.startsWith("/login");
  const isLandingAds = location.pathname.startsWith("/landing-ads");

  useEffect(() => {
    document.body.classList.toggle("landing", isLandingAds);
    return () => document.body.classList.remove("landing");
  }, [isLandingAds]);

  useEffect(() => {
    document.body.classList.toggle("home", isHome);
    document.body.classList.toggle("internal", !isHome);
    return () => document.body.classList.remove("home", "internal");
  }, [isHome]);

  useEffect(() => {
    const THRESHOLD = 80;
    const update = () => setScrolled(window.scrollY > THRESHOLD);
    requestAnimationFrame(update);
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  const onNavClick = useCallback((e) => {
    const a = e.target.closest("a[href]");
    if (!a) return;
    const url = new URL(a.href, window.location.origin);
    if (url.origin === window.location.origin && url.pathname === "/") {
      const hash = url.hash.replace("#", "");
      if (hash) {
        e.preventDefault();
        closeMobileMenu();
        if (!isHome) {
          navigate("/", { replace: false });
          setTimeout(() => smoothScrollToId(hash), 0);
        } else {
          smoothScrollToId(hash);
        }
        return;
      }
    }
    closeMobileMenu();
  }, [isHome, navigate]);

  const activeKey = useMemo(() => {
    if (location.pathname.startsWith("/livros")) return "livros";
    if (location.pathname.startsWith("/youtube")) return "youtube";
    if (location.pathname.startsWith("/admin")) return "admin";
    if (isHome) {
      const h = location.hash.replace("#", "");
      if (["servicos", "sobre", "youtube", "contacto"].includes(h)) return h;
      return "home";
    }
    return "";
  }, [location.pathname, location.hash, isHome]);

  const navbarClass =
    "navbar navbar-expand-lg main-navbar fixed-top navbar-dark " +
    (isHome ? (scrolled ? "is-scrolled" : "is-top") : "is-scrolled");
  const logoSrc = (isLandingAds && !scrolled) ? LogoLight : LogoDark;

  return (
    <>
      <a href="#conteudo" className="visually-hidden-focusable">Ir para o conteúdo</a>

      <div id="color-top" className="animated" />

      <nav
        className={navbarClass}
        style={{ transition: "background-color .25s ease, box-shadow .25s ease" }}
        onClick={onNavClick}
      >
        <div className="container site-navbar">
          <Link className="navbar-brand d-flex align-items-center gap-2" to="/" aria-label="Página inicial">
            <img
              src={logoSrc}
              alt="Luciana Costa"
              width={56}
              height={56}
              className="logo-img"
              style={{ height: 56, width: 56, objectFit: "contain", borderRadius: 12 }}
            />
            <span className="brand-stack d-flex flex-column lh-1">
              <span className="fw-semibold brand-name">Luciana Costa</span>
              <span className="brand-sub text-muted small">
                Médium &amp; Terapeuta Holística
              </span>
            </span>
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navMain"
            aria-controls="navMain"
            aria-expanded="false"
            aria-label="Alternar navegação"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse justify-content-end" id="navMain">
            <ul className="navbar-nav align-items-lg-center">
              <li className="nav-item">
                <a className={`nav-link ${activeKey === "home" ? "active" : ""}`} href="/">Início</a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${activeKey === "sobre" ? "active" : ""}`} href="/#sobre">Quem sou</a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${activeKey === "servicos" ? "active" : ""}`} href="/#servicos">Terapias</a>
              </li>
              <li className="nav-item">
                <Link className={`nav-link ${location.pathname.startsWith("/livros") ? "active" : ""}`} to="/livros">
                  Livros
                </Link>
              </li>
              <li className="nav-item">
                <Link className={`nav-link ${activeKey === "youtube" ? "active" : ""}`} to="/youtube">
                  YouTube
                </Link>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${activeKey === "contacto" ? "active" : ""}`} href="/#contacto">Contacto</a>
              </li>

              {utilizador?.tipo === "admin" && (
                <li className="nav-item">
                  <Link className="nav-link" to="/admin">Admin</Link>
                </li>
              )}

              <li className="nav-item ms-lg-2">
                <CartBadge className="btn btn-outline-light btn-sm btn-icon rounded-pill d-flex align-items-center gap-2 position-relative btn-cart" />
              </li>

              {utilizador ? (
                <li className="nav-item dropdown ms-2">
                  <button
                    className="btn btn-outline-light btn-sm nav-userchip dropdown-toggle no-caret"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <UserGlyph className="me-2 user-glyph" width={18} height={18} />
                    <span className="user-email">{displayName(utilizador)}</span>
                    <span className="chev ms-2" aria-hidden>▾</span>
                  </button>
                  {/* <ul className="dropdown-menu dropdown-menu-end user-menu shadow-lg">
                    {utilizador?.tipo === "admin" && (
                      <li><Link className="dropdown-item" to="/admin">Área Admin</Link></li>
                    )}
                    <li><button className="dropdown-item" onClick={handleLogout}>Sair</button></li>
                  </ul> */}
                  <ul className="dropdown-menu dropdown-menu-end user-menu shadow-lg">
                    {/* Link para a área de cliente */}
                    <li>
                      <Link className="dropdown-item" to="/conta">
                        A minha conta
                      </Link>
                    </li>

                    {utilizador?.tipo === "admin" && (
                      <li>
                        <Link className="dropdown-item" to="/admin">
                          Área Admin
                        </Link>
                      </li>
                    )}

                    <li>
                      <button className="dropdown-item" onClick={handleLogout}>
                        Sair
                      </button>
                    </li>
                  </ul>

                </li>
              ) : (
                <li className="nav-item ms-lg-2">
                  <NavLoginButton onClick={() => navigate("/login")} />
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>

      {!isHome && !isLogin && <div className="content-spacer" />}

      <main id="conteudo" tabIndex={-1}>
        {isHome ? <Outlet /> : <div><Outlet /></div>}
      </main>

      <Footer />

      <WhatsAppButton />
      <div id="wp-custom-whatsapp-button" className="floating-whatsapp">
        <a
          href="https://wa.me/2389394185?text=Merc%C3%AAs%2C+preciso+de+ajuda%2C+como+posso+fazer+uma+pergunta+ao+Tarot%3F"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir WhatsApp para perguntar"
        >
          <FaWhatsapp size={28} color="#fff" />
          <span>Pergunta Aqui</span>
        </a>
      </div>
      {/* <CookieConsent /> */}
    </>
  );
}
